import type { z } from "zod";
import type { TiptapJsonNode } from "./rich-text";

/** Every block kind the registry defines. Kept equal to Prisma `BlockType` (contract test). */
export const BLOCK_KIND_TYPES = [
	"TEXT",
	"IMAGE",
	"VIDEO",
	"CODE",
	"CALLOUT",
	"DIVIDER",
	"STATISTIC",
	"PROCESS",
	"QUIZ",
	"POLL",
	"MOTION_GRAPHIC",
	"REVEAL",
	"COMPARISON",
	"REFLECTION",
] as const;

export type BlockKindType = (typeof BLOCK_KIND_TYPES)[number];

/** Kinds a block can be switched to in place; structured kinds (PROCESS, QUIZ, P14 kinds) need their own flows. */
export const TYPE_SWITCHABLE_BLOCK_KIND_TYPES = [
	"TEXT",
	"IMAGE",
	"VIDEO",
	"CODE",
	"CALLOUT",
	"DIVIDER",
	"STATISTIC",
] as const satisfies readonly BlockKindType[];

export type TypeSwitchableBlockKindType =
	(typeof TYPE_SWITCHABLE_BLOCK_KIND_TYPES)[number];

/**
 * How a block sits in the card partitioner:
 * - `stackable`: several share a card (counts `textWeight` toward the per-card cap);
 * - `full_card`: gets a card of its own;
 * - `process`: PROCESS steps, laid out by `cardLayout`.
 */
export type BlockPartitionRole = "stackable" | "full_card" | "process";

/** Facts the timeline knows about a block beyond its content. */
export interface BlockDwellContext {
	/** Playable media length when known (VIDEO). */
	knownMediaDurationMs?: number | null;
}

/**
 * An interaction a learner can have with a block, which a gate can require (R14, P14). Quiz answers
 * are read from `QuizResponse`; every other kind is recorded as a `TopicBlockInteraction`.
 */
export const BLOCK_GATE_INTERACTION_KINDS = [
	"quiz.answer",
	"poll.vote",
	"reveal.open",
	"comparison.view",
	"reflection.submit",
] as const;
export type BlockGateInteractionKind =
	(typeof BLOCK_GATE_INTERACTION_KINDS)[number];

/** Kinds a learner records through `learn.recordTopicBlockInteraction` (never `quiz.answer`). */
export type RecordedBlockInteractionKind = Exclude<
	BlockGateInteractionKind,
	"quiz.answer"
>;

/** A required interaction; a quiz answer names its question (a QUIZ block may ask several, D53). */
export type BlockGateInteraction =
	| { kind: RecordedBlockInteractionKind; questionId?: undefined }
	| { kind: "quiz.answer"; questionId: string };

/** One interaction a kind accepts from learners, recorded whether or not its gate requires it. */
export interface BlockInteractionSpec {
	kind: RecordedBlockInteractionKind;
	/**
	 * Client payload → the stored payload, checked against the block's content (e.g. a poll option
	 * must exist on the poll); null rejects it.
	 */
	parsePayload(
		content: Record<string, unknown>,
		raw: unknown,
	): Record<string, unknown> | null;
	/** A later record replaces the stored payload (reflection); otherwise the first one stands (poll vote). */
	revisable: boolean;
}

/**
 * How a block appears in a PDF (print mode, P14; exports in P24):
 * - `static`: its static rendering;
 * - `question_and_options`: the prompt with every option listed (POLL);
 * - `revealed`: the hidden content shown open (REVEAL);
 * - `side_by_side`: both sides next to each other (COMPARISON);
 * - `poster_and_link`: a still frame plus a link to play it (VIDEO, MOTION_GRAPHIC);
 * - `prompt_and_lines`: the prompt with blank lines to write on (REFLECTION).
 */
export const BLOCK_PDF_EXPORT_MODES = [
	"static",
	"question_and_options",
	"revealed",
	"side_by_side",
	"poster_and_link",
	"prompt_and_lines",
] as const;
export type BlockPdfExportMode = (typeof BLOCK_PDF_EXPORT_MODES)[number];

export interface BlockExportPolicy {
	pdf: BlockPdfExportMode;
}

/** Block-level TopicPatch ops (P6, R17). */
export const BLOCK_PATCH_OP_TYPES = [
	"block.insert",
	"block.remove",
	"block.move",
	"block.setType",
	"block.setContent",
	"block.setSettings",
	"block.setMotion",
] as const;

export type BlockPatchOpType = (typeof BLOCK_PATCH_OP_TYPES)[number];

/** Every block op: kinds that can be switched in place. */
export const ALL_BLOCK_PATCH_OPS: readonly BlockPatchOpType[] =
	BLOCK_PATCH_OP_TYPES;

/** Structured kinds (PROCESS steps, QUIZ question refs, P14 kinds) need their own flows, so they cannot switch type. */
export const STRUCTURED_BLOCK_PATCH_OPS: readonly BlockPatchOpType[] =
	BLOCK_PATCH_OP_TYPES.filter((op) => op !== "block.setType");

/**
 * How a block appears in the whole-topic doc (P7, R18):
 * - `text`: its body is edited inline as TipTap JSON; `toDoc` reads the body from content (canonical:
 *   `toDoc(fromDoc(c, body))` is what the editor shows after a save) and `fromDoc` writes an edited
 *   body back over the previous content, keeping its other fields;
 * - `atom`: rendered by the kind's node view and edited through its own flow, never inline.
 */
export type BlockDocNode =
	| { mode: "atom" }
	| {
			mode: "text";
			toDoc(content: Record<string, unknown>): TiptapJsonNode[];
			fromDoc(
				previous: Record<string, unknown>,
				body: readonly unknown[],
			): Record<string, unknown>;
	  };

export interface BlockKindDefinition<
	TType extends BlockKindType = BlockKindType,
	TContent extends Record<string, unknown> = Record<string, unknown>,
	TSettings extends Record<string, unknown> = Record<string, unknown>,
> {
	type: TType;
	/** Canonical persisted `Block.content` (strict: no legacy or generation-only keys). */
	contentSchema: z.ZodType<TContent>;
	defaultContent: TContent;
	/** Per-block settings (`Block.settings`); validated on write from P6. */
	settingsSchema: z.ZodType<TSettings>;
	defaultSettings: TSettings;
	/** TopicPatch ops this kind accepts (P6); the patch applier refuses any other. */
	patchOps: readonly BlockPatchOpType[];
	/** Its node in the whole-topic doc (P7). */
	docNode: BlockDocNode;
	partitionRole: BlockPartitionRole;
	/** Content-dependent override of `partitionRole` (e.g. CALLOUT `example` / `quote` take a full card). */
	resolvePartitionRole?(content: Record<string, unknown>): BlockPartitionRole;
	/** Weight toward the per-card stackable text cap (DIVIDER = 0). */
	textWeight: number;
	/**
	 * Written (LLM-generated or editor) content → canonical content: unknown keys are dropped and
	 * canonical keys only are read (legacy aliases were rewritten by the P3 migration). Total: never
	 * throws; missing required text becomes "" so the result always satisfies `contentSchema`.
	 */
	normalizeContent(raw: Record<string, unknown>): TContent;
	/**
	 * Speakable narration text. Reads assembled content, which may carry hydrated, non-persisted
	 * fields (QUIZ question and answers).
	 */
	deriveNarrationText(content: Record<string, unknown>): string;
	/**
	 * Time a learner needs on this block (R13): reading its visible text plus any interaction hold.
	 * Summed per card by the timeline compiler, which is the only caller.
	 */
	estimateDwellMs(
		content: Record<string, unknown>,
		context: BlockDwellContext,
	): number;
	/** Interactions the card gate requires for this block (R14); `[]` for passive kinds. */
	gate(content: Record<string, unknown>): readonly BlockGateInteraction[];
	/** Interactions learners can record on this block (P14); `[]` for passive kinds and QUIZ. */
	interactions: readonly BlockInteractionSpec[];
	/** How the block exports (P14 print mode; P24). */
	exportPolicy: BlockExportPolicy;
}

export function defineBlockKind<
	TType extends BlockKindType,
	TContent extends Record<string, unknown>,
	TSettings extends Record<string, unknown>,
>(
	definition: BlockKindDefinition<TType, TContent, TSettings>,
): BlockKindDefinition<TType, TContent, TSettings> {
	return definition;
}
